import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Textarea } from '@shared/components/ui/textarea';
import { Label } from '@shared/components/ui/label';
import { Badge } from '@shared/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select';
import { Switch } from '@shared/components/ui/switch';
import { Alert, AlertDescription } from '@shared/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/components/ui/tabs';
import {
  Plus,
  Edit2, // kept — used inside editor modal
  Trash2,
  FileText,
  CheckSquare,
  Type,
  List,
  ToggleLeft,
  Calendar,
  AlertCircle,
  Save,
  Eye,
  Upload,
  FileVideo,
  X,
  Globe,
  Building2,
  GitBranch,
} from 'lucide-react';
import { supabase } from '@shared/supabaseClient';
import { useAuth } from '@shared/contexts/AuthContext';
import { useToast } from '@shared/components/ui/use-toast';

/**
 * Induction Brief Editor
 * Create and manage induction brief templates with questions OR file uploads (PDF/Video)
 */
const InductionBriefEditor = () => {
  const { user } = useAuth();
  // Current month in YYYY-MM format — used as default version for new briefs
  const currentYYYYMM = new Date().toISOString().slice(0, 7);
  const [briefs, setBriefs] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [currentBrief, setCurrentBrief] = useState(null);
  const [previewBrief, setPreviewBrief] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  // Publish New Version dialog
  const [isVersionDialogOpen, setIsVersionDialogOpen] = useState(false);
  const [briefToVersion, setBriefToVersion] = useState(null);
  const [publishingVersion, setPublishingVersion] = useState(false);
  const [versionData, setVersionData] = useState({
    version: '',
    changelog: '',
  });

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    version: currentYYYYMM,
    is_active: true,
    is_mandatory: false,
    depot_id: null, // null = All Sites
    brief_type: 'questions', // 'questions' | 'pdf' | 'video'
    file_url: null,
    file_size_bytes: null,
    file_duration_seconds: null,
    questions: [],
  });

  // Question being edited
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionFormData, setQuestionFormData] = useState({
    type: 'text',
    question_text: '',
    is_required: true,
    options: [],
  });

  useEffect(() => {
    loadBriefs();
    loadDepots();
  }, [user]);

  const loadDepots = async () => {
    try {
      const { data, error } = await supabase
        .from('depots')
        .select('id, name, code')
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      setDepots(data || []);
    } catch (error) {
      console.error('Error loading depots:', error);
    }
  };

  const loadBriefs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('induction_briefs')
        .select('*, depots(id, name)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Transform content JSONB to questions array for UI
      const transformedData = (data || []).map(brief => ({
        ...brief,
        depot_name: brief.depots?.name || 'No Site',
        questions: brief.content?.sections || [],
        description: brief.content?.description || '',
      }));
      
      setBriefs(transformedData);
    } catch (error) {
      console.error('Error loading briefs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load induction briefs',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (brief = null) => {
    if (brief) {
      setCurrentBrief(brief);
      setFormData({
        title: brief.title,
        description: brief.description,
        version: brief.version,
        is_active: brief.is_active,
        is_mandatory: brief.is_mandatory || false,
        depot_id: brief.depot_id || null,
        brief_type: brief.file_type === 'pdf' ? 'pdf' : brief.file_type === 'video' ? 'video' : 'questions',
        file_url: brief.file_url || null,
        file_size_bytes: brief.file_size_bytes || null,
        file_duration_seconds: brief.file_duration_seconds || null,
        questions: [...brief.questions],
      });
    } else {
      setCurrentBrief(null);
      setFormData({
        title: '',
        description: '',
        version: currentYYYYMM,
        is_active: true,
        is_mandatory: false,
        depot_id: user.depot_id, // Default to user's depot
        brief_type: 'questions',
        file_url: null,
        file_size_bytes: null,
        file_duration_seconds: null,
        questions: [],
      });
    }
    setIsEditorOpen(true);
  };

  const openQuestionEditor = (question = null, index = null) => {
    if (question) {
      setEditingQuestion(index);
      setQuestionFormData({
        type: question.type,
        question_text: question.question_text,
        is_required: question.is_required,
        options: question.options || [],
      });
    } else {
      setEditingQuestion(null);
      setQuestionFormData({
        type: 'text',
        question_text: '',
        is_required: true,
        options: [],
      });
    }
  };

  const saveQuestion = () => {
    if (!questionFormData.question_text.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Question text is required',
        variant: 'destructive',
      });
      return;
    }

    const newQuestion = {
      id: editingQuestion !== null ? formData.questions[editingQuestion].id : `q-${Date.now()}`,
      ...questionFormData,
    };

    if (editingQuestion !== null) {
      // Update existing question
      const updatedQuestions = [...formData.questions];
      updatedQuestions[editingQuestion] = newQuestion;
      setFormData({ ...formData, questions: updatedQuestions });
    } else {
      // Add new question
      setFormData({ ...formData, questions: [...formData.questions, newQuestion] });
    }

    // Reset question form
    setEditingQuestion(null);
    setQuestionFormData({
      type: 'text',
      question_text: '',
      is_required: true,
      options: [],
    });

    toast({
      title: 'Question Saved',
      description: editingQuestion !== null ? 'Question updated' : 'Question added',
    });
  };

  const deleteQuestion = (index) => {
    const updatedQuestions = formData.questions.filter((_, i) => i !== index);
    setFormData({ ...formData, questions: updatedQuestions });
    toast({
      title: 'Question Deleted',
      description: 'Question removed from brief',
    });
  };

  const moveQuestion = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.questions.length) return;

    const updatedQuestions = [...formData.questions];
    [updatedQuestions[index], updatedQuestions[newIndex]] = [updatedQuestions[newIndex], updatedQuestions[index]];
    setFormData({ ...formData, questions: updatedQuestions });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileType = formData.brief_type;
    
    // Validate file type
    if (fileType === 'pdf' && file.type !== 'application/pdf') {
      toast({
        title: 'Invalid File Type',
        description: 'Please upload a PDF file',
        variant: 'destructive',
      });
      return;
    }

    if (fileType === 'video' && !file.type.startsWith('video/')) {
      toast({
        title: 'Invalid File Type',
        description: 'Please upload a video file (MP4, WebM)',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size
    const maxSize = fileType === 'video' ? 100 * 1024 * 1024 : 10 * 1024 * 1024; // 100MB for video, 10MB for PDF
    if (file.size > maxSize) {
      toast({
        title: 'File Too Large',
        description: `Maximum file size is ${fileType === 'video' ? '100MB' : '10MB'}`,
        variant: 'destructive',
      });
      return;
    }

    setUploadingFile(true);
    try {
      // Upload to Supabase Storage
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `induction-briefs/${timestamp}-${sanitizedFileName}`;

      const { data, error } = await supabase.storage
        .from('company-assets')
        .upload(filePath, file, {
          contentType: file.type,
          upsert: false,
        });

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('company-assets')
        .getPublicUrl(filePath);

      // For video files, try to get duration
      let duration = null;
      if (fileType === 'video') {
        try {
          duration = await getVideoDuration(file);
        } catch (err) {
          console.warn('Could not get video duration:', err);
        }
      }

      setFormData({
        ...formData,
        file_url: urlData.publicUrl,
        file_size_bytes: file.size,
        file_duration_seconds: duration,
      });

      toast({
        title: 'File Uploaded',
        description: `${file.name} uploaded successfully`,
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload file',
        variant: 'destructive',
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const getVideoDuration = (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(Math.round(video.duration));
      };
      video.onerror = () => reject(new Error('Could not load video'));
      video.src = URL.createObjectURL(file);
    });
  };

  const removeFile = () => {
    setFormData({
      ...formData,
      file_url: null,
      file_size_bytes: null,
      file_duration_seconds: null,
    });
  };

  const saveBrief = async () => {
    if (!formData.title.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Brief title is required',
        variant: 'destructive',
      });
      return;
    }

    // Validate based on brief type
    if (formData.brief_type === 'questions' && formData.questions.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'At least one question is required',
        variant: 'destructive',
      });
      return;
    }

    if ((formData.brief_type === 'pdf' || formData.brief_type === 'video') && !formData.file_url) {
      toast({
        title: 'Validation Error',
        description: `Please upload a ${formData.brief_type === 'pdf' ? 'PDF' : 'video'} file`,
        variant: 'destructive',
      });
      return;
    }

    try {
      // Transform questions back to content JSONB format
      const contentData = formData.brief_type === 'questions' ? {
        sections: formData.questions,
        description: formData.description,
      } : {};

      const briefData = {
        depot_id: formData.depot_id,
        title: formData.title,
        content: contentData,
        is_active: formData.is_active,
        is_mandatory: formData.is_mandatory,
        file_type: formData.brief_type === 'questions' ? 'json' : formData.brief_type,
        file_url: formData.file_url,
        file_size_bytes: formData.file_size_bytes,
        file_duration_seconds: formData.file_duration_seconds,
        updated_at: new Date().toISOString(),
      };

      if (currentBrief) {
        // Update existing brief
        const { error } = await supabase
          .from('induction_briefs')
          .update(briefData)
          .eq('id', currentBrief.id);

        if (error) throw error;

        toast({
          title: 'Brief Updated',
          description: `${formData.title} has been updated`,
        });
      } else {
        // Create new brief.
        // If this brief is being saved as active, first deactivate any existing
        // active brief with the same title at the same depot — the new one supersedes it.
        if (briefData.is_active) {
          let deactivateQuery = supabase
            .from('induction_briefs')
            .update({ is_active: false, updated_at: new Date().toISOString() })
            .eq('title', formData.title)
            .eq('is_active', true);

          if (formData.depot_id) {
            deactivateQuery = deactivateQuery.eq('depot_id', formData.depot_id);
          } else {
            deactivateQuery = deactivateQuery.is('depot_id', null);
          }

          const { error: deactivateError } = await deactivateQuery;
          if (deactivateError) {
            console.warn('Could not deactivate old brief:', deactivateError);
          }
        }

        const { error } = await supabase
          .from('induction_briefs')
          .insert({
            ...briefData,
            version: formData.version || currentYYYYMM,
            valid_from: new Date().toISOString().split('T')[0],
            created_by: user.id,
          });

        if (error) {
          if (error.message?.includes('duplicate key') || error.message?.includes('unique constraint')) {
            throw new Error(
              `A brief named "${formData.title}" at version ${formData.version || currentYYYYMM} already exists for this site. ` +
              `Change the version (e.g. ${currentYYYYMM}) or use a different title.`
            );
          }
          throw error;
        }

        toast({
          title: 'Brief Created',
          description: `${formData.title} has been created`,
        });
      }

      setIsEditorOpen(false);
      await loadBriefs();
    } catch (error) {
      console.error('Error saving brief:', error);
      toast({
        title: 'Error saving brief',
        description: error?.message || 'Failed to save induction brief',
        variant: 'destructive',
      });
    }
  };

  const duplicateBrief = async (brief) => {
    try {
      const contentData = {
        sections: brief.questions || brief.content?.sections || [],
        description: brief.description || brief.content?.description || '',
      };

      const { error } = await supabase
        .from('induction_briefs')
        .insert({
          depot_id: user.depot_id,
          title: `${brief.title} (Copy)`,
          version: 1,
          content: contentData,
          is_active: false,
          valid_from: new Date().toISOString().split('T')[0],
          created_by: user.id,
        });

      if (error) throw error;

      toast({
        title: 'Brief Duplicated',
        description: 'A copy has been created',
      });
      await loadBriefs();
    } catch (error) {
      console.error('Error duplicating brief:', error);
      toast({
        title: 'Error',
        description: 'Failed to duplicate brief',
        variant: 'destructive',
      });
    }
  };

  const openVersionDialog = (brief) => {
    const now = new Date();
    const defaultVersion = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setBriefToVersion(brief);
    setVersionData({ version: defaultVersion, changelog: '' });
    setIsVersionDialogOpen(true);
  };

  const handlePublishNewVersion = async () => {
    if (!briefToVersion) return;
    if (!versionData.version.trim()) {
      toast({ title: 'Version required', description: 'Please enter a version (e.g. 2026-03)', variant: 'destructive' });
      return;
    }

    setPublishingVersion(true);
    try {
      // 1. Insert new brief row — same content, new version, parent = old brief.id
      const contentData = briefToVersion.brief_type === 'questions'
        ? { sections: briefToVersion.questions || [], description: briefToVersion.description || '' }
        : {};

      const { error: insertErr } = await supabase
        .from('induction_briefs')
        .insert({
          depot_id:             briefToVersion.depot_id,
          title:                briefToVersion.title,
          version:              versionData.version.trim(),
          content:              contentData,
          is_active:            true,
          is_mandatory:         briefToVersion.is_mandatory,
          file_type:            briefToVersion.brief_type === 'questions' ? 'json' : briefToVersion.brief_type,
          file_url:             briefToVersion.file_url || null,
          file_size_bytes:      briefToVersion.file_size_bytes || null,
          file_duration_seconds: briefToVersion.file_duration_seconds || null,
          valid_from:           new Date().toISOString().split('T')[0],
          parent_brief_id:      briefToVersion.id,
          changelog:            versionData.changelog.trim() || null,
          created_by:           user.id,
        });

      if (insertErr) throw insertErr;

      // 2. Mark old brief as superseded (is_active = false)
      const { error: updateErr } = await supabase
        .from('induction_briefs')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', briefToVersion.id);

      if (updateErr) throw updateErr;

      toast({
        title: 'New version published',
        description: `${briefToVersion.title} → v${versionData.version.trim()}. All future invitations will use the new version. Previous completions of the old version are preserved.`,
      });

      setIsVersionDialogOpen(false);
      setBriefToVersion(null);
      await loadBriefs();
    } catch (err) {
      console.error('Publish version error:', err);
      toast({ title: 'Error', description: err.message || 'Failed to publish new version', variant: 'destructive' });
    } finally {
      setPublishingVersion(false);
    }
  };

  const deleteBrief = async (briefId) => {
    if (!confirm('Are you sure you want to delete this induction brief? All related submissions, passes and team member links will also be deleted.')) return;

    try {
      // 1. Get the submission IDs for this brief
      const { data: submissions, error: fetchErr } = await supabase
        .from('induction_submissions')
        .select('id')
        .eq('brief_id', briefId);

      if (fetchErr) throw fetchErr;

      const submissionIds = (submissions || []).map(s => s.id);

      if (submissionIds.length > 0) {
        // 2. Get pass IDs for these submissions
        const { data: passes, error: passFetchErr } = await supabase
          .from('passes')
          .select('id')
          .in('induction_submission_id', submissionIds);
        if (passFetchErr) throw passFetchErr;

        const passIds = (passes || []).map(p => p.id);

        if (passIds.length > 0) {
          // 3. Delete site_sessions that reference passes (FK: site_sessions_pass_id_fkey)
          const { error: ssErr } = await supabase
            .from('site_sessions')
            .delete()
            .in('pass_id', passIds);
          if (ssErr) throw ssErr;

          // 4. Delete the passes
          const { error: passErr } = await supabase
            .from('passes')
            .delete()
            .in('id', passIds);
          if (passErr) throw passErr;
        }

        // 5. Null out project_team_members.induction_submission_id
        const { error: tmErr } = await supabase
          .from('project_team_members')
          .update({ induction_submission_id: null })
          .in('induction_submission_id', submissionIds);
        if (tmErr) throw tmErr;

        // 6. Delete the submissions themselves
        const { error: subErr } = await supabase
          .from('induction_submissions')
          .delete()
          .in('id', submissionIds);
        if (subErr) throw subErr;
      }

      // 5. Delete the brief
      const { error } = await supabase
        .from('induction_briefs')
        .delete()
        .eq('id', briefId);

      if (error) throw error;

      toast({
        title: 'Brief Deleted',
        description: 'Induction brief has been deleted',
      });
      await loadBriefs();
    } catch (error) {
      console.error('Error deleting brief:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete brief',
        variant: 'destructive',
      });
    }
  };

  const getQuestionIcon = (type) => {
    switch (type) {
      case 'text':
        return <Type className="h-4 w-4" />;
      case 'textarea':
        return <FileText className="h-4 w-4" />;
      case 'checkbox':
        return <CheckSquare className="h-4 w-4" />;
      case 'select':
        return <List className="h-4 w-4" />;
      case 'radio':
        return <ToggleLeft className="h-4 w-4" />;
      default:
        return <Type className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading briefs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Induction Briefs</h1>
          <p className="text-muted-foreground mt-1">
            Create and manage induction brief templates
          </p>
        </div>
        <Button onClick={() => openEditor()} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Create Brief
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Briefs</p>
                <p className="text-2xl font-bold">{briefs.length}</p>
              </div>
              <FileText className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-green-500">
                  {briefs.filter(b => b.is_active).length}
                </p>
              </div>
              <CheckSquare className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Superseded</p>
                <p className="text-2xl font-bold text-orange-500">
                  {briefs.filter(b => !b.is_active).length}
                </p>
              </div>
              <AlertCircle className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Brief List */}
      {briefs.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No induction briefs</h3>
              <p className="text-muted-foreground mt-2">
                Create your first induction brief template
              </p>
              <Button onClick={() => openEditor()} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Create Brief
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        (() => {
          // Group briefs by depot
          const grouped = briefs.reduce((acc, brief) => {
            const key = brief.depot_id || '__none__';
            const label = brief.depot_name || 'No Site';
            if (!acc[key]) acc[key] = { label, briefs: [] };
            acc[key].briefs.push(brief);
            return acc;
          }, {});

          return (
            <div className="space-y-6">
              {Object.entries(grouped).map(([depotKey, group]) => (
                <div key={depotKey}>
                  {/* Site header */}
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{group.label}</h2>
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-xs text-muted-foreground">{group.briefs.length} brief{group.briefs.length !== 1 ? 's' : ''}</span>
                  </div>

                  <Card className="overflow-hidden">
                    {/* Column headers */}
                    <div className="grid grid-cols-[1fr_96px_108px_352px] px-5 py-2.5 border-b bg-muted/50 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                      <span>Brief</span>
                      <span className="text-center">Version</span>
                      <span className="text-center">Status</span>
                      <span className="text-right">Actions</span>
                    </div>

                    <div className="divide-y">
                      {group.briefs.map((brief) => (
                        <div
                          key={brief.id}
                          className="grid grid-cols-[1fr_96px_108px_352px] px-5 py-3 items-center hover:bg-muted/20 transition-colors"
                        >
                          {/* Title + meta */}
                          <div className="pr-6 min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="font-medium text-sm truncate">{brief.title}</span>
                              {brief.changelog && (
                                <span className="text-xs text-muted-foreground italic truncate hidden lg:block">↳ {brief.changelog}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                Updated {new Date(brief.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </span>
                              {brief.brief_type === 'questions' && (
                                <span className="flex items-center gap-1">
                                  <CheckSquare className="h-3 w-3" />
                                  {brief.questions.length} questions
                                </span>
                              )}
                              {brief.brief_type === 'pdf' && (
                                <span className="flex items-center gap-1">
                                  <FileText className="h-3 w-3" />
                                  PDF · {(brief.file_size_bytes / 1024 / 1024).toFixed(1)} MB
                                </span>
                              )}
                              {brief.brief_type === 'video' && (
                                <span className="flex items-center gap-1">
                                  <FileVideo className="h-3 w-3" />
                                  Video · {(brief.file_size_bytes / 1024 / 1024).toFixed(1)} MB
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Version */}
                          <div className="flex justify-center">
                            <Badge variant="outline" className="font-mono text-xs whitespace-nowrap">
                              v{brief.version}
                            </Badge>
                          </div>

                          {/* Status */}
                          <div className="flex justify-center">
                            <Badge variant={brief.is_active ? 'default' : 'secondary'} className="text-xs whitespace-nowrap">
                              {brief.is_active ? 'Active' : 'Superseded'}
                            </Badge>
                          </div>

                          {/* Actions — fixed 352px so rows all align */}
                          <div className="flex gap-1.5 justify-end items-center">
                            <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs whitespace-nowrap"
                              onClick={() => { setPreviewBrief(brief); setIsPreviewOpen(true); }}>
                              <Eye className="h-3 w-3 mr-1" />Preview
                            </Button>
                            <div className="w-[106px] flex justify-end">
                              {brief.is_active && (
                                <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs whitespace-nowrap text-primary border-primary/40 hover:bg-primary/5 w-full"
                                  onClick={() => openVersionDialog(brief)}>
                                  <GitBranch className="h-3 w-3 mr-1" />New Version
                                </Button>
                              )}
                            </div>
                            <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-600 shrink-0"
                              onClick={() => deleteBrief(brief.id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          );
        })()
      )}

      {/* Brief Editor Modal */}
      <Dialog open={isEditorOpen} onOpenChange={setIsEditorOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {currentBrief ? 'Edit Induction Brief' : 'Create Induction Brief'}
            </DialogTitle>
            <DialogDescription>
              Create a template with questions, upload a PDF, or add a video brief
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Brief Details */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Brief Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Site Safety Induction"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Overview of safety requirements and site rules"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="depot_id">Applicable Site</Label>
                  <Select
                    value={formData.depot_id || 'all'}
                    onValueChange={(value) => setFormData({ ...formData, depot_id: value === 'all' ? null : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a site">
                        {formData.depot_id
                          ? (() => {
                              const d = depots.find(dep => dep.id === formData.depot_id);
                              return d ? (
                                <div className="flex items-center gap-2">
                                  <Building2 className="h-4 w-4" />
                                  {d.name} ({d.code})
                                </div>
                              ) : null;
                            })()
                          : (
                            <div className="flex items-center gap-2">
                              <Globe className="h-4 w-4" />
                              All Sites
                            </div>
                          )
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4" />
                          All Sites
                        </div>
                      </SelectItem>
                      {depots.map((depot) => (
                        <SelectItem key={depot.id} value={depot.id}>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4" />
                            {depot.name} ({depot.code})
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="version">Version</Label>
                  <Input
                    id="version"
                    value={formData.version}
                    onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                    placeholder="2026-02"
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Format: YYYY-MM (e.g. 2026-02)</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="is_active">Active (available for use)</Label>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    id="is_mandatory"
                    checked={formData.is_mandatory}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_mandatory: checked })}
                  />
                  <Label htmlFor="is_mandatory">Mandatory (required for digital badge)</Label>
                </div>
              </div>

              {formData.is_mandatory && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    This brief must be completed before contractors can receive their digital badge/QR code.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* Brief Type Tabs */}
            <div className="border-t pt-4">
              <Tabs value={formData.brief_type} onValueChange={(value) => setFormData({ ...formData, brief_type: value })}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="questions">
                    <CheckSquare className="h-4 w-4 mr-2" />
                    Questions
                  </TabsTrigger>
                  <TabsTrigger value="pdf">
                    <FileText className="h-4 w-4 mr-2" />
                    PDF Document
                  </TabsTrigger>
                  <TabsTrigger value="video">
                    <FileVideo className="h-4 w-4 mr-2" />
                    Video Brief
                  </TabsTrigger>
                </TabsList>

                {/* Questions Tab */}
                <TabsContent value="questions" className="space-y-4 mt-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Questions ({formData.questions.length})</h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openQuestionEditor()}
                      className="flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add Question
                    </Button>
                  </div>

                  {formData.questions.length === 0 ? (
                    <div className="text-center py-8 bg-muted/50 rounded-lg">
                      <CheckSquare className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No questions added yet</p>
                      <Button
                        variant="link"
                    onClick={() => openQuestionEditor()}
                    className="mt-2"
                  >
                    Add your first question
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {formData.questions.map((question, index) => (
                    <Card key={question.id}>
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {getQuestionIcon(question.type)}
                              <span className="text-xs text-muted-foreground uppercase">
                                {question.type}
                              </span>
                              {question.is_required && (
                                <Badge variant="outline" className="text-xs">Required</Badge>
                              )}
                            </div>
                            <p className="font-medium">{question.question_text}</p>
                            {question.options && question.options.length > 0 && (
                              <p className="text-sm text-muted-foreground mt-1">
                                Options: {question.options.join(', ')}
                              </p>
                            )}
                          </div>

                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => moveQuestion(index, 'up')}
                              disabled={index === 0}
                            >
                              ↑
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => moveQuestion(index, 'down')}
                              disabled={index === formData.questions.length - 1}
                            >
                              ↓
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openQuestionEditor(question, index)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteQuestion(index)}
                              className="text-red-500"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

            {/* Question Editor (inline) */}
            {editingQuestion !== null || questionFormData.question_text ? (
              <div className="border-t pt-4 bg-muted/50 p-4 rounded-lg space-y-4">
                <h4 className="font-semibold">
                  {editingQuestion !== null ? 'Edit Question' : 'New Question'}
                </h4>

                <div>
                  <Label htmlFor="question_type">Question Type</Label>
                  <Select
                    value={questionFormData.type}
                    onValueChange={(value) => setQuestionFormData({ ...questionFormData, type: value })}
                  >
                    <SelectTrigger id="question_type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">Short Text</SelectItem>
                      <SelectItem value="textarea">Long Text</SelectItem>
                      <SelectItem value="checkbox">Checkbox</SelectItem>
                      <SelectItem value="select">Dropdown</SelectItem>
                      <SelectItem value="radio">Multiple Choice</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="question_text">Question Text *</Label>
                  <Input
                    id="question_text"
                    value={questionFormData.question_text}
                    onChange={(e) => setQuestionFormData({ ...questionFormData, question_text: e.target.value })}
                    placeholder="Enter your question"
                  />
                </div>

                {['select', 'radio'].includes(questionFormData.type) && (
                  <div>
                    <Label htmlFor="options">Options (comma-separated)</Label>
                    <Input
                      id="options"
                      value={questionFormData.options.join(', ')}
                      onChange={(e) => setQuestionFormData({ 
                        ...questionFormData, 
                        options: e.target.value.split(',').map(o => o.trim()).filter(o => o)
                      })}
                      placeholder="Option 1, Option 2, Option 3"
                    />
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <Switch
                    id="is_required"
                    checked={questionFormData.is_required}
                    onCheckedChange={(checked) => setQuestionFormData({ ...questionFormData, is_required: checked })}
                  />
                  <Label htmlFor="is_required">Required question</Label>
                </div>

                <div className="flex gap-2">
                  <Button onClick={saveQuestion}>
                    <Save className="h-4 w-4 mr-2" />
                    Save Question
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditingQuestion(null);
                      setQuestionFormData({
                        type: 'text',
                        question_text: '',
                        is_required: true,
                        options: [],
                      });
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : null}
          </TabsContent>

          {/* PDF Tab */}
          <TabsContent value="pdf" className="space-y-4 mt-4">
                  <div className="text-center py-8 bg-muted/50 rounded-lg">
                    {formData.file_url ? (
                      <div className="space-y-4">
                        <FileText className="h-12 w-12 text-primary mx-auto" />
                        <div>
                          <p className="font-medium">PDF Uploaded</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            Size: {(formData.file_size_bytes / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <div className="flex gap-2 justify-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(formData.file_url, '_blank')}
                          >
                            Preview PDF
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={removeFile}
                            className="text-red-500"
                          >
                            <X className="h-4 w-4 mr-2" />
                            Remove
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <FileText className="h-12 w-12 text-muted-foreground mx-auto" />
                        <div>
                          <p className="font-medium">No PDF uploaded</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            Upload a PDF document (max 10MB)
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingFile}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {uploadingFile ? 'Uploading...' : 'Upload PDF'}
                        </Button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="application/pdf"
                          className="hidden"
                          onChange={handleFileUpload}
                        />
                      </div>
                    )}
                  </div>
          </TabsContent>

          {/* Video Tab */}
          <TabsContent value="video" className="space-y-4 mt-4">
                  <div className="text-center py-8 bg-muted/50 rounded-lg">
                    {formData.file_url ? (
                      <div className="space-y-4">
                        <FileVideo className="h-12 w-12 text-primary mx-auto" />
                        <div>
                          <p className="font-medium">Video Uploaded</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            Size: {(formData.file_size_bytes / 1024 / 1024).toFixed(2)} MB
                            {formData.file_duration_seconds && (
                              <> • Duration: {Math.floor(formData.file_duration_seconds / 60)}:{(formData.file_duration_seconds % 60).toString().padStart(2, '0')}</>
                            )}
                          </p>
                        </div>
                        <div className="flex gap-2 justify-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(formData.file_url, '_blank')}
                          >
                            Preview Video
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={removeFile}
                            className="text-red-500"
                          >
                            <X className="h-4 w-4 mr-2" />
                            Remove
                          </Button>
                        </div>
                        <Alert>
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>
                            Users must watch at least 80% of the video to complete this brief.
                          </AlertDescription>
                        </Alert>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <FileVideo className="h-12 w-12 text-muted-foreground mx-auto" />
                        <div>
                          <p className="font-medium">No video uploaded</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            Upload a video file - MP4, WebM (max 100MB)
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingFile}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {uploadingFile ? 'Uploading...' : 'Upload Video'}
                        </Button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="video/mp4,video/webm"
                          className="hidden"
                          onChange={handleFileUpload}
                        />
                      </div>
                    )}
                  </div>
          </TabsContent>
        </Tabs>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditorOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={saveBrief} 
              disabled={
                !formData.title || 
                (formData.brief_type === 'questions' && formData.questions.length === 0) ||
                ((formData.brief_type === 'pdf' || formData.brief_type === 'video') && !formData.file_url)
              }
            >
              <Save className="h-4 w-4 mr-2" />
              Save Brief
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewBrief?.title}</DialogTitle>
            <DialogDescription>{previewBrief?.description}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {previewBrief?.questions.map((question, index) => (
              <div key={question.id} className="p-4 border rounded-lg">
                <div className="flex items-start gap-2 mb-2">
                  <span className="font-semibold">{index + 1}.</span>
                  <div className="flex-1">
                    <p className="font-medium">
                      {question.question_text}
                      {question.is_required && <span className="text-red-500 ml-1">*</span>}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Type: {question.type}
                    </p>
                  </div>
                </div>
                
                {/* Mock input based on type */}
                <div className="mt-3 ml-6">
                  {question.type === 'text' && (
                    <Input placeholder="Answer..." disabled />
                  )}
                  {question.type === 'textarea' && (
                    <Textarea placeholder="Answer..." disabled rows={3} />
                  )}
                  {question.type === 'checkbox' && (
                    <div className="flex items-center gap-2">
                      <input type="checkbox" disabled />
                      <span className="text-sm text-muted-foreground">I agree</span>
                    </div>
                  )}
                  {question.type === 'select' && (
                    <Select disabled>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an option..." />
                      </SelectTrigger>
                    </Select>
                  )}
                  {question.type === 'radio' && question.options && (
                    <div className="space-y-2">
                      {question.options.map((option, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <input type="radio" disabled name={`q-${question.id}`} />
                          <span className="text-sm">{option}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button onClick={() => setIsPreviewOpen(false)}>Close Preview</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish New Version Dialog */}
      <Dialog open={isVersionDialogOpen} onOpenChange={setIsVersionDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="h-5 w-5 text-primary" />
              Publish New Version
            </DialogTitle>
            <DialogDescription>
              Publish an updated version of <strong>{briefToVersion?.title}</strong>.
              The current version will be marked as superseded. Anyone who already
              completed the old version will be required to re-read and re-sign when
              next invited. Anyone who previously completed <em>this same version</em> will
              be auto-approved on future invitations.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="pub-version">New Version *</Label>
              <p className="text-xs text-muted-foreground mb-1">
                Use YYYY-MM format (e.g. 2026-03). Current version: v{briefToVersion?.version}
              </p>
              <Input
                id="pub-version"
                value={versionData.version}
                onChange={(e) => setVersionData({ ...versionData, version: e.target.value })}
                placeholder="2026-03"
                className="font-mono"
              />
            </div>

            <div>
              <Label htmlFor="pub-changelog">What changed? (optional)</Label>
              <p className="text-xs text-muted-foreground mb-1">
                Brief note shown on the brief card so the team knows why it was updated.
              </p>
              <Textarea
                id="pub-changelog"
                value={versionData.changelog}
                onChange={(e) => setVersionData({ ...versionData, changelog: e.target.value })}
                placeholder="e.g. Updated emergency evacuation procedures, added new PPE requirements."
                rows={3}
              />
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                After publishing: all <strong>new</strong> invitations will use this version.
                Existing in-progress submissions will not be affected.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsVersionDialogOpen(false)}
              disabled={publishingVersion}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePublishNewVersion}
              disabled={publishingVersion || !versionData.version.trim()}
            >
              {publishingVersion ? 'Publishing...' : (
                <>
                  <GitBranch className="h-4 w-4 mr-2" />
                  Publish v{versionData.version || '…'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InductionBriefEditor;
